import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { Badge, withStyles } from '@material-ui/core';
import { useQuery } from '@tanstack/react-query';

import { adminListOffersCountApi } from '../../api/offers/adminListOffersCountApi';
import { supportCountReservationsForReviewApi } from '../../api/reservations/supportCountReservationsForReviewApi';
import { adminGetUnreadUserComplaintCountApi } from '../../api/supports/adminGetUnreadUserComplaintCountApi';
import { adminListAgenciesCountApi } from '../../api/venues/adminListAgenciesCountApi';
import { OfferStatus, ReservsationsSupportListType, VenueStatus } from '../../graphql/API';
import { useChatSupportTicketCounts } from '../../hooks/reactQuery/chat/useChatSupportTicketCounts';
import { useAdminRole } from '../../hooks/useAdminRole';
import { COMPLAINT_TYPES } from '../Tables/SupportsTable/types';
import {
  menuItemsAdmin,
  menuItemsCityManager,
  menuItemsFastCheck,
  menuItemsSocial,
  menuItemsSupport,
  menuVenue,
} from './menuItems';

const StyledBadge = withStyles((theme) => ({
  badge: {
    right: 16,
    top: 0,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    backgroundColor: 'rgba(241, 13, 0, 1)',
  },
}))(Badge);

const MobileSidebar = (): JSX.Element => {
  const {
    isSupport,
    isCityManager,
    isVenue,
    venueId,
    isSocial,
    isAdmin,
    isFastCheck,
    isSuperAdmin,
  } = useAdminRole();

  const { data: supportUnreadCount } = useQuery({
    queryKey: ['list-support', 'support-count', 'unread-count'],
    queryFn: () => adminGetUnreadUserComplaintCountApi({ types: COMPLAINT_TYPES }),
    enabled: isAdmin,
  });

  const { userSupportCount, venueSupportCount } = useChatSupportTicketCounts({
    type: '',
  });

  const { data: offersCount } = useQuery({
    queryKey: ['offers-pending-count'],
    queryFn: () =>
      adminListOffersCountApi({
        status: OfferStatus.pending,
        search: '',
        sortBy: 'createdAt',
        sortDirection: 'desc',
        marketID: '',
        category: null,
      }),
    enabled: isAdmin,
  });

  const { data: pendingAgenciesCount } = useQuery({
    queryKey: ['pending-agencies-count'],
    queryFn: () =>
      adminListAgenciesCountApi({
        status: VenueStatus.pending,
      }),
    enabled: isAdmin,
  });

  const { data: fastChecksCount } = useQuery({
    queryKey: ['fast-check-count-mobile'],
    queryFn: () =>
      supportCountReservationsForReviewApi({
        type: ReservsationsSupportListType.pending,
      }),
    enabled: !isSocial,
  });

  const totalFastCheckPending = React.useMemo(
    () =>
      fastChecksCount?.reduce((total, item) => {
        return total + (item.count ?? 0);
      }, 0),
    [fastChecksCount],
  );

  const menuVenueMemo = React.useMemo(() => {
    return menuVenue(venueId);
  }, [venueId]);

  const counts = {
    supportCount: supportUnreadCount,
    offerPendingCount: offersCount ? offersCount['totalPending'] : undefined,
    pendingAgenciesCount,
    unreadChatCount: (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
    fastCheckPendingCount: totalFastCheckPending,
  };

  const menuGroups = isSupport
    ? menuItemsSupport(counts)
    : isSocial
    ? menuItemsSocial
    : isCityManager
    ? menuItemsCityManager(counts)
    : isFastCheck
    ? menuItemsFastCheck(counts)
    : isVenue
    ? menuVenueMemo
    : menuItemsAdmin(counts, { superAdmin: isSuperAdmin });

  return (
    <SidebarContainer>
      {menuGroups.map((group) => (
        <Section key={group.heading}>
          <SectionHeading>{group.heading}</SectionHeading>
          {group.tabs.map((item) => (
            <SidebarItem
              key={item.route}
              to={item.route}
              activeClassName="active"
              exact={item.exact}
            >
              <StyledBadge badgeContent={item.count} color="secondary">
                <item.icon />
              </StyledBadge>
              <Label>{item.label}</Label>
            </SidebarItem>
          ))}
        </Section>
      ))}
    </SidebarContainer>
  );
};

export default MobileSidebar;

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const SectionHeading = styled.h6`
  color: #12193c;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0 0 8px 1rem;
  padding: 0;
`;

const SidebarItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  padding-left: 1rem;
  color: inherit;
  text-decoration: none;
  position: relative;

  svg {
    margin-right: 1rem;
  }

  &.active {
    border-left: 4px solid #3f51b5;
    padding-left: calc(1rem - 4px); /* Adjust padding to account for border width */
  }
`;

const Label = styled.span`
  color: #111111;
  font-size: 15px;
  font-family: Poppins;
  font-weight: 400;
  word-wrap: break-word;
`;
